<!--
  HospitalSearchKeywordManage.vue
  병원검색 role별 제외 키워드 관리 페이지

  주요 기능:
  - role별 탭(병원/제조사/환자)으로 제외 키워드 관리
  - 키워드 추가 / 삭제
-->
<template>
  <div class="container-fluid container-wrap col-sm-12 col-xs-12">
    <div style="margin-top: 28px"></div>

    <!-- 페이지 헤더 -->
    <div class="card-header pb-0 col-sm-12 d-flex">
      <div class="col-sm-12 col-xs-12">
        <div class="col-sm-12 col-xs-12 justify-content-between titleContainer">
          <div class="title-row">
            <h1 class="fw-bold page-title">병원검색 제외 키워드 관리</h1>
          </div>
          <span class="pageDesc">
            role별로 등록한 키워드가 병원명에 포함되면 병원찾기 검색 결과에서
            제외됩니다.
          </span>
        </div>
      </div>
    </div>

    <!-- 콘텐츠 -->
    <div class="px-0 pb-2">
      <div class="card content-wrap-card p-3">
        <a-tabs v-model:activeKey="activeRole">
          <a-tab-pane
            v-for="tab in roleTabs"
            :key="tab.value"
            :tab="tab.label"
          />
        </a-tabs>

        <!-- 키워드 추가 -->
        <div class="add-row">
          <a-input
            v-model:value="newKeyword"
            placeholder="제외할 키워드를 입력하세요 (예: 기공소)"
            :maxlength="100"
            style="max-width: 320px"
            @pressEnter="handleAdd"
          />
          <a-button type="primary" :loading="adding" @click="handleAdd">
            추가
          </a-button>
        </div>

        <!-- 키워드 목록 -->
        <a-table
          :columns="columns"
          :data-source="keywordList"
          :loading="loading"
          :pagination="false"
          row-key="_id"
          size="middle"
          class="mt-3"
          :locale="{ emptyText: '등록된 키워드가 없습니다.' }"
        >
          <template #bodyCell="{ column, record }">
            <template v-if="column.key === 'action'">
              <a-popconfirm
                title="이 키워드를 삭제하시겠습니까?"
                ok-text="삭제"
                cancel-text="취소"
                @confirm="handleDelete(record)"
              >
                <a-button danger size="small" :loading="deletingId === record._id">삭제</a-button>
              </a-popconfirm>
            </template>
          </template>
        </a-table>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, watch } from 'vue';
import { message } from 'ant-design-vue';
import HospitalSearchKeywordApi from './api';

export default {
  name: 'HospitalSearchKeywordManage',
  setup() {
    const roleTabs = [
      { label: '병원', value: 'HOSPITAL' },
      { label: '제조사', value: 'MANUFACTURER' },
      { label: '환자', value: 'CUSTOMER' },
    ];

    const columns = [
      {
        title: '키워드',
        dataIndex: 'keyword',
        key: 'keyword',
      },
      {
        title: '등록일',
        dataIndex: 'createdAt',
        key: 'createdAt',
        customRender: ({ text }) =>
          text ? new Date(text).toLocaleString('ko-KR') : '-',
        width: 200,
      },
      {
        title: '관리',
        key: 'action',
        width: 100,
      },
    ];

    const activeRole = ref('HOSPITAL');
    const keywordList = ref([]);
    const newKeyword = ref('');
    const loading = ref(false);
    const adding = ref(false);
    const deletingId = ref(null);

    const handleError = (error, defaultMessage) => {
      console.error('Error occurred:', error);
      const msg =
        error.response?.data?.message || error.message || defaultMessage;
      message.error(Array.isArray(msg) ? msg.join(', ') : msg);
    };

    const loadKeywords = async () => {
      loading.value = true;
      try {
        const response = await HospitalSearchKeywordApi.getList(
          activeRole.value,
        );
        keywordList.value = Array.isArray(response.data) ? response.data : [];
      } catch (error) {
        handleError(error, '키워드 목록을 불러오지 못했습니다.');
        keywordList.value = [];
      } finally {
        loading.value = false;
      }
    };

    const handleAdd = async () => {
      const keyword = newKeyword.value.trim();
      if (!keyword) {
        message.warning('키워드를 입력해주세요.');
        return;
      }
      adding.value = true;
      try {
        await HospitalSearchKeywordApi.create({
          role: activeRole.value,
          keyword,
        });
        message.success('키워드가 등록되었습니다.');
        newKeyword.value = '';
        await loadKeywords();
      } catch (error) {
        handleError(error, '키워드 등록에 실패했습니다.');
      } finally {
        adding.value = false;
      }
    };

    const handleDelete = async (record) => {
      deletingId.value = record._id;
      try {
        await HospitalSearchKeywordApi.remove(record._id);
        message.success('키워드가 삭제되었습니다.');
        await loadKeywords();
      } catch (error) {
        handleError(error, '키워드 삭제에 실패했습니다.');
      } finally {
        deletingId.value = null;
      }
    };

    watch(activeRole, loadKeywords);
    onMounted(loadKeywords);

    return {
      roleTabs,
      columns,
      activeRole,
      keywordList,
      newKeyword,
      loading,
      adding,
      deletingId,
      loadKeywords,
      handleAdd,
      handleDelete,
    };
  },
};
</script>

<style scoped lang="scss">
.container-wrap {
  padding: 0 16px;
}

.titleContainer {
  display: flex;
  flex-direction: column;
  margin-bottom: 20px;
}

.title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 16px;
}

.page-title {
  color: #000;
  font-size: 1.8rem;
  margin-bottom: 0;
}

.pageDesc {
  color: #6c757d;
  font-size: 14px;
  margin-top: 8px;
}

.add-row {
  display: flex;
  gap: 8px;
  align-items: center;
  margin-top: 8px;
}
</style>
